import { defineStore } from 'pinia'
import { setToken } from '@/utils/auth'
import { setStore } from '@/utils/store'
import { getShoppingCartList, queryMallProductByEnums } from '@/api/mall/product'
import { getPageStore } from '@/api/common'
import type { CurrencyInfo, CartProductItem, CartStoreItem } from '@/views/mall/types'

const useUserStore = defineStore('user', {
	// 类似于Vuex中的state，存储数据
	state: () => {
		return {
			token: '', //用户token
			userInfo: {} as any, //用户信息
			menuList: [] as any[], //左侧菜单
			currentMeta: {}, //当前路由meta
			language: 'en', //多语言简写zh en
			langIndex: 2,
			languageStr: 'English（US）',
			langConfigList: [] as any[], //多语言配置列表
			cartQuantity: 0, //购物车数量
			isShowLogin: false, //是否显示登录弹窗
			currencyInfo: {
				code: 'USD',
				message: '美元',
				value: 'USD',
				sign: '$'
			} as CurrencyInfo,
			storeInfo: {
				// TODO: 先全局写死，后续应从实际门店选择逻辑获取
				storeName: {
					f: 'test1',
					s: '',
					t: '',
					value: 'test1'
				},
				storeId: '1753295508308516633',
				storeCode: 'Sto20260701000001414'
			},
			orders: [] as CartProductItem[],
			storeQuantity: 0, //门店数量
			useOnlineStock: false //是否使用线上库存
		}
	},

	// 类似于Vuex中的getters，用于派生数据
	getters: {
		currencySign: (state) => state.currencyInfo?.sign,
		currencyValue: (state) => state.currencyInfo?.value
	},

	//类似于Vuex中的actions和mutations的组合，用于修改state
	actions: {
		setToken(val: string) {
			setToken(val)
			this.token = val
			this.getCartQuantity()
		},
		setUserInfo(val: any) {
			this.userInfo = val
		},
		setCurrencyInfo(val: CurrencyInfo) {
			this.currencyInfo = val
		},
		// 根据语言设置对应货币：zh→人民币，其他语言→美元
		setCurrencyByLanguage(lang: string) {
			this.currencyInfo =
				lang === 'zh'
					? { code: 'CNY', message: '人民币', value: 'CNY', sign: '￥' }
					: { code: 'USD', message: '美元', value: 'USD', sign: '$' }
		},
		setOrder(val: CartProductItem[]) {
			this.orders = val
		},
		/** 查询 ERP 配置并设置商品库存来源。 */
		async loadProductStockConfig() {
			try {
				const res: any = await queryMallProductByEnums({ enums: 'ALLOW_SALES_WHEN_STOCK_NOT_ENOUGH' })
				const config = res?.data?.[0]
				const value = config?.value?.value ?? config?.value
				this.useOnlineStock = String(value).toUpperCase() === 'YES'
			} catch {
				// 配置查询失败时默认使用 ERP 库存。
			}
		},
		setUpdateLogin() {
			this.isShowLogin = !this.isShowLogin
		},
		setMenuList(val: any) {
			this.menuList = val
		},
		setCurrentMeta(val: any) {
			this.currentMeta = val
		},
		setLanguage(val: any) {
			setStore({ name: 'language', content: val })
			this.language = val
		},
		setLangIndex(val: any) {
			setStore({ name: 'langIndex', content: val })
			this.langIndex = val
		},
		setLanguageStr(val: any) {
			setStore({ name: 'languageStr', content: val })
			this.languageStr = val
		},
		setLangConfigList(val: any[]) {
			this.langConfigList = val
		},
		clearLogin() {
			this.token = ''
			setToken('')
			this.userInfo = {}
			this.menuList = []
			this.currentMeta = {}
		},

		// 获取购物车列表
		async getCartQuantity() {
			if (!this.userInfo?.id) return
			try {
				const res = await getShoppingCartList()
				if (res.code === 200) {
					let list = res.data || []
					list = list.flatMap((val: CartStoreItem) => val.productList ?? [])
					this.cartQuantity = list.reduce((sum: number, item: CartProductItem) => sum + item.quantity, 0)
				}
			} catch {
				// 静默处理，避免阻断调用链
			}
		},

		// 获取门店数量
		async getStoreQuantity() {
			try {
				const res: any = await getPageStore({ pageNo: 1, pageSize: 100 })
				if (res.code === 200) {
					this.storeQuantity = (res.data.records || []).length
				}
			} catch {
				// 静默处理，避免阻断调用链
			}
		}
	}
})

export default useUserStore
